import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createAccountAssetType,
  deleteAccountAssetType,
  listAccountAssetTypes,
  updateAccountAssetType,
} from "../../api/endpoints/accountAssetTypes.js";
import { filterBySearch } from "../services/entityFilters.js";

export function useAccountAssetTypes(accountId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    if (!accountId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await listAccountAssetTypes(accountId);
      setItems(data);
    } catch (err) {
      setError(err.message ?? "Failed to load asset types");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const visible = useMemo(
    () => filterBySearch(items, search, ["name"]),
    [items, search],
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((assetType) => {
    setEditing(assetType);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const saveAssetType = useCallback(async (payload) => {
    if (!accountId) return;
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const updated = await updateAccountAssetType(accountId, editing.id, payload);
        setItems((prev) => prev.map((row) => (row.id === editing.id ? updated : row)));
      } else {
        const created = await createAccountAssetType(accountId, payload);
        setItems((prev) => [...prev, created]);
      }
      closeModal();
    } catch (err) {
      setError(err.message ?? "Failed to save asset type");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId, editing, closeModal]);

  const removeAssetType = useCallback(async (assetTypeId) => {
    if (!accountId) return;
    setSaving(true);
    setError(null);
    try {
      await deleteAccountAssetType(accountId, assetTypeId);
      setItems((prev) => prev.filter((row) => row.id !== assetTypeId));
    } catch (err) {
      setError(err.message ?? "Failed to delete asset type");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  return {
    items: visible,
    allItems: items,
    loading,
    saving,
    error,
    search,
    setSearch,
    modalOpen,
    editing,
    openCreate,
    openEdit,
    closeModal,
    saveAssetType,
    removeAssetType,
    reload,
    total: items.length,
  };
}
